import type { Ctx } from '@milkdown/kit/ctx';
import type { Mark } from '@milkdown/kit/prose/model';
import type { EditorView } from '@milkdown/kit/prose/view';
import { linkSchema } from '@milkdown/kit/preset/commonmark';
import { TextSelection } from '@milkdown/kit/prose/state';
import { editorViewCtx } from '@milkdown/kit/core';

import { TENTATIVE_LINK_META, CONFIRM_LINK_META, CLEANUP_TENTATIVE_LINK_META } from './tentative-link-plugin';

export interface LinkInfo {
  from: number;
  to: number;
  mark: Mark;
  href: string;
}

/**
 * Find link mark at current cursor position or within selection
 */
export function getLinkAtCursor(view: EditorView, ctx: Ctx): LinkInfo | null {
  // Guard: schema may not be available during initial render
  let linkType;
  try {
    linkType = linkSchema.type(ctx);
  } catch {
    return null;
  }

  const { state } = view;
  const { selection } = state;
  const { $from, from, to } = selection;

  // First check marks at cursor position
  let linkMark = $from.marks().find((mark) => mark.type === linkType);

  // If not found and we have a range selection, check if there's a link in the range
  if (!linkMark && from !== to) {
    state.doc.nodesBetween(from, to, (node) => {
      if (node.isText && !linkMark) {
        const mark = node.marks.find((m) => m.type === linkType);
        if (mark) linkMark = mark;
      }

      return !linkMark; // stop if found
    });
  }

  if (!linkMark) return null;

  // Find the full extent of the link
  let linkFrom = from;
  let linkTo = to;

  // Walk through the document to find link boundaries
  state.doc.nodesBetween(Math.max(0, from - 200), Math.min(state.doc.content.size, to + 200), (node, pos) => {
    if (node.isText) {
      const hasLink = node.marks.some((m) => m.type === linkType && m.attrs.href === linkMark!.attrs.href);
      if (hasLink) {
        if (pos < from) linkFrom = Math.min(linkFrom, pos);
        if (pos + node.nodeSize > to) linkTo = Math.max(linkTo, pos + node.nodeSize);
      }
    }

    return true;
  });

  return { from: linkFrom, to: linkTo, mark: linkMark, href: linkMark.attrs.href as string };
}

/**
 * Add or update a link mark on the current selection.
 * Adds CONFIRM_LINK_META to prevent tentative link cleanup.
 */
export function applyLinkMark(ctx: Ctx, href: string, from: number, to: number): void {
  const view = ctx.get(editorViewCtx);
  const linkType = linkSchema.type(ctx);

  const { tr } = view.state;
  // Remove existing link marks first
  tr.removeMark(from, to, linkType);
  // Add new link
  tr.addMark(from, to, linkType.create({ href }));
  // Mark this transaction as confirming the link so plugin doesn't remove it
  tr.setMeta(CONFIRM_LINK_META, { from, to });

  view.dispatch(tr);
}

/**
 * Remove link mark from the specified range
 */
export function removeLinkMark(ctx: Ctx, from: number, to: number): void {
  const view = ctx.get(editorViewCtx);
  const linkType = linkSchema.type(ctx);

  const { tr } = view.state;
  tr.removeMark(from, to, linkType);

  view.dispatch(tr);
}

/**
 * Get current text selection range
 */
export function getSelectionBounds(ctx: Ctx): { from: number; to: number; isEmpty: boolean } {
  const view = ctx.get(editorViewCtx);
  const { selection } = view.state;

  return {
    from: selection.from,
    to: selection.to,
    isEmpty: selection.empty,
  };
}

/**
 * Set text selection in the editor
 */
export function setSelectionBounds(ctx: Ctx, from: number, to?: number): void {
  const view = ctx.get(editorViewCtx);
  const { tr } = view.state;
  view.dispatch(tr.setSelection(TextSelection.create(view.state.doc, from, to ?? from)));
}

/**
 * Request cleanup of tentative (empty) links. Call when the user dismisses the link tooltip
 * without confirming (Escape or click away). The tentative-link plugin will remove empty link marks.
 */
export function requestTentativeLinkCleanup(ctx: Ctx): void {
  const view = ctx.get(editorViewCtx);
  view.dispatch(view.state.tr.setMeta(CLEANUP_TENTATIVE_LINK_META, true));
}

/**
 * Toggle link mark on current selection.
 * If selection has link → remove it. Otherwise → add empty link (tooltip will show for editing).
 * Marks empty link creation as tentative so plugin can clean it up if abandoned.
 * No-op if selection is empty.
 */
export function toggleLinkMark(ctx: Ctx): void {
  const view = ctx.get(editorViewCtx);
  const { state } = view;
  const { selection } = state;
  const { from, to } = selection;

  if (selection.empty) return;

  const linkType = linkSchema.type(ctx);
  const hasLink = state.doc.rangeHasMark(from, to, linkType);

  const { tr } = state;
  if (hasLink) {
    tr.removeMark(from, to, linkType);
  } else {
    tr.addMark(from, to, linkType.create({ href: '' }));
    // Mark this as a tentative link that should be cleaned up if not confirmed
    tr.setMeta(TENTATIVE_LINK_META, { from, to });
  }
  view.dispatch(tr);
}
